import { typography, radius } from '@/shared/theme/tokens';
import { withAlpha } from '../../liveModeMeta';
import { BCAST } from './broadcastMeta';

// The on-air kicker above the headline — the channel's framing of the state.
const KICKER = {
  degraded: 'SIGNAL ADVISORY',
  critical: 'BREAKING',
  actions:  'DEVELOPING',
  watching: 'MARKET WATCH',
  nominal:  'STANDING BY',
};

/**
 * The breaking-news headline bar — the single biggest piece of real estate, read
 * from across the room. Its colour and intensity track the adaptive headline
 * state (CRITICAL = urgent red, ACTIONS = active amber, NOMINAL = calm green).
 * Shows the state kicker, big label, the count, and the subtext (e.g. the
 * degraded "LAST VALID …" line). Pulses only when CRITICAL or DEGRADED.
 */
export function BreakingBar({ headline, animate }) {
  const { color, label, count, code, subtext, state, icon } = headline;
  const urgent = state === 'critical' || state === 'degraded';
  const kicker = KICKER[state] ?? 'LIVE';

  return (
    <div style={{
      flexShrink: 0, display: 'flex', alignItems: 'stretch', minHeight: 92,
      borderBottom: `2px solid ${color}`,
      // Directional broadcast sheen — strong state tint at the kicker, fading to
      // the studio black under the subtext.
      background: `linear-gradient(90deg, ${withAlpha(color, '30')} 0%, ${withAlpha(color, '16')} 48%, ${BCAST.studio} 100%)`,
    }}>
      {/* Kicker block — solid state colour, the "BREAKING" flag */}
      <div style={{
        flexShrink: 0, width: 168, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 5, padding: '0 12px',
        background: color, color: BCAST.studio,
        animation: animate && urgent ? 'bc-pulse 1.25s steps(1,end) infinite' : 'none',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 30 }}>{icon}</span>
        <span style={{
          fontFamily: BCAST.heading, fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.extrabold, letterSpacing: typography.letterSpacing.widest,
          textTransform: 'uppercase',
        }}>
          {kicker}
        </span>
      </div>

      {/* Headline body */}
      <div style={{
        flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 22, padding: '0 26px',
      }}>
        <span style={{
          flexShrink: 0, fontFamily: BCAST.mono, fontSize: typography.fontSize.base,
          fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wider,
          color, border: `1px solid ${withAlpha(color, '88')}`, borderRadius: radius.xs,
          padding: '4px 9px',
        }}>
          {code}
        </span>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{
            fontFamily: BCAST.heading, fontSize: typography.fontSize['7xl'],
            fontWeight: typography.fontWeight.extrabold, letterSpacing: typography.letterSpacing.wide,
            color, lineHeight: 1, textTransform: 'uppercase',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {label}
          </span>
          {subtext && (
            <span style={{
              fontFamily: BCAST.mono, fontSize: typography.fontSize.sm, color: BCAST.inkDim,
              letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
              fontVariantNumeric: 'tabular-nums',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {subtext}
            </span>
          )}
        </div>

        {count > 0 && (
          <div style={{
            flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', minWidth: 86, padding: '0 14px',
            borderLeft: `1px solid ${withAlpha(color, '44')}`,
          }}>
            <span style={{
              fontFamily: BCAST.mono, fontSize: typography.fontSize['7xl'],
              fontWeight: typography.fontWeight.extrabold, color,
              fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            }}>
              {count}
            </span>
            <span style={{
              fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, color: BCAST.inkFaint,
              letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', marginTop: 3,
            }}>
              {state === 'critical' ? 'CRITICAL' : state === 'watching' ? 'WATCHING' : 'OPEN'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
